import React from 'react';
import * as Three from 'three';
import {GeometryUtils} from '../utils/export';

function lenFromProps(element, keys, fallback) {
  for (let i = 0; i < keys.length; i++) {
    const v = element.getIn(['properties', keys[i], 'length']);
    if (typeof v === 'number' && !Number.isNaN(v) && v > 0) return v;
  }
  return fallback;
}

function render2DMissing(element, layer) {
  const proto = element.get('prototype');
  const typeLabel = element.get('type') || '';

  switch (proto) {
    case 'items': {
      const w = lenFromProps(element, ['width'], 200);
      const d = lenFromProps(element, ['depth', 'height'], 100);
      return React.createElement(
        'g',
        {opacity: 0.6},
        React.createElement('rect', {
          x: -w / 2,
          y: -d / 2,
          width: w,
          height: d,
          fill: '#d8d8d8',
          stroke: '#888',
          strokeDasharray: '6 4'
        }),
        React.createElement('text', {
          x: 0,
          y: 4,
          textAnchor: 'middle',
          fill: '#444',
          fontSize: 11,
          fontFamily: 'sans-serif'
        }, typeLabel)
      );
    }
    case 'lines': {
      const v0 = layer.vertices.get(element.getIn(['vertices', 0]));
      const v1 = layer.vertices.get(element.getIn(['vertices', 1]));
      if (!v0 || !v1) return React.createElement('g', null);
      const len = GeometryUtils.pointsDistance(v0.x, v0.y, v1.x, v1.y);
      const thickness = lenFromProps(element, ['thickness'], 18);
      return React.createElement('rect', {
        x: 0,
        y: -thickness / 2,
        width: len,
        height: thickness,
        fill: '#d0d0d0',
        stroke: '#777',
        strokeDasharray: '4 3'
      });
    }
    case 'holes': {
      const w = lenFromProps(element, ['width'], 80);
      return React.createElement('rect', {
        x: -w / 2,
        y: -18,
        width: w,
        height: 36,
        fill: '#e8e8e8',
        stroke: '#666',
        strokeDasharray: '4 3'
      });
    }
    case 'areas': {
      const verts = element.get('vertices');
      if (!verts || !verts.size) return React.createElement('g', null);
      const pts = verts
        .toArray()
        .map(vid => {
          const v = layer.vertices.get(vid);
          return v ? `${v.x},${v.y}` : null;
        })
        .filter(Boolean)
        .join(' ');
      if (!pts) return React.createElement('g', null);
      return React.createElement('polygon', {
        points: pts,
        fill: 'rgba(200,200,200,0.35)',
        stroke: '#888',
        strokeDasharray: '5 4'
      });
    }
    default:
      return React.createElement('g', null);
  }
}

function itemPlaceholder3D(element) {
  const w = lenFromProps(element, ['width'], 200);
  const d = lenFromProps(element, ['depth', 'height'], 100);
  const h = lenFromProps(element, ['height'], 60);
  const geo = new Three.BoxGeometry(w, h, d);
  const mat = new Three.MeshLambertMaterial({
    color: 0xaaaaaa,
    transparent: true,
    opacity: 0.5
  });
  const mesh = new Three.Mesh(geo, mat);
  mesh.position.y = h / 2;
  return mesh;
}

function linePlaceholder3D(element, layer) {
  const v0 = layer.vertices.get(element.getIn(['vertices', 0]));
  const v1 = layer.vertices.get(element.getIn(['vertices', 1]));
  const len = v0 && v1 ? GeometryUtils.pointsDistance(v0.x, v0.y, v1.x, v1.y) : 100;
  const thickness = lenFromProps(element, ['thickness'], 20);
  const height = lenFromProps(element, ['height'], 100);
  const geo = new Three.BoxGeometry(len, height, thickness);
  const mat = new Three.MeshLambertMaterial({color: 0xbdbdbd, transparent: true, opacity: 0.55});
  const mesh = new Three.Mesh(geo, mat);
  mesh.position.set(len / 2, height / 2, 0);
  return mesh;
}

function holePlaceholder3D(element) {
  const w = lenFromProps(element, ['width'], 80);
  const h = lenFromProps(element, ['height'], 200);
  const t = lenFromProps(element, ['thickness'], 30);
  const geo = new Three.BoxGeometry(w, h, t);
  const mat = new Three.MeshLambertMaterial({color: 0xcfcfcf, transparent: true, opacity: 0.5});
  const mesh = new Three.Mesh(geo, mat);
  mesh.position.y = h / 2;
  return mesh;
}

function areaPlaceholder3D(element, layer) {
  const verts = element.get('vertices');
  if (!verts || !verts.size) {
    const mesh = new Three.Mesh(
      new Three.PlaneGeometry(100, 100),
      new Three.MeshLambertMaterial({color: 0xcccccc, side: Three.DoubleSide, transparent: true, opacity: 0.4})
    );
    mesh.rotation.x = -Math.PI / 2;
    return mesh;
  }
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  verts.forEach(vid => {
    const v = layer.vertices.get(vid);
    if (!v) return;
    minX = Math.min(minX, v.x);
    maxX = Math.max(maxX, v.x);
    minY = Math.min(minY, v.y);
    maxY = Math.max(maxY, v.y);
  });
  const dx = Math.max(maxX - minX, 10);
  const dy = Math.max(maxY - minY, 10);
  const mesh = new Three.Mesh(
    new Three.PlaneGeometry(dx, dy),
    new Three.MeshLambertMaterial({color: 0xcccccc, side: Three.DoubleSide, transparent: true, opacity: 0.35})
  );
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.set((minX + maxX) / 2, 0.5, -(minY + maxY) / 2);
  return mesh;
}

function render3DMissing(element, layer) {
  const proto = element.get('prototype');
  let mesh;
  switch (proto) {
    case 'items':
      mesh = itemPlaceholder3D(element);
      break;
    case 'lines':
      mesh = linePlaceholder3D(element, layer);
      break;
    case 'holes':
      mesh = holePlaceholder3D(element);
      break;
    case 'areas':
      mesh = areaPlaceholder3D(element, layer);
      break;
    default:
      mesh = new Three.Mesh(
        new Three.BoxGeometry(50, 50, 50),
        new Three.MeshLambertMaterial({color: 0xbbbbbb})
      );
  }
  return Promise.resolve(mesh);
}

export function createUnknownCatalogStub(unknownType) {
  return {
    name: unknownType,
    prototype: 'items',
    info: {
      tag: [],
      title: unknownType,
      description: 'Removed or unknown catalog entry',
      image: ''
    },
    properties: {},
    render2D(element, layer, scene) {
      return render2DMissing(element, layer);
    },
    render3D(element, layer, sceneData) {
      return render3DMissing(element, layer);
    }
  };
}
